import type { SelectDropdownProps } from '@byecode/ui'
import { Divider, Empty, Flex, Group, IconFont, Input, Menu, Option, Select, SelectDropdown, Tag, Text } from '@byecode/ui'
import type { ApplicationSettingLanguageItem, BasePage, PageAbstract, PageItem } from '@lighthouse/core'
import { set } from 'date-fns'
import { findIndex, reduce } from 'rambda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { LOGIN_PAGE, NO_NAME_PAGE } from '../../constants'
import { floatPageTree } from '../../utils'
import { SelectItem } from './SelectItem'
import * as SC from './styles'

export type HandlePageInfo = { id: string; name?: string }

interface SelectPageProps {
    value?: string
    width?: number
    languageList?: ApplicationSettingLanguageItem[]
    options: PageItem[]
    placeholder?: string
    readOnly?: boolean
    searchable?: boolean
    withinPortal?: boolean
    dropdownProps?: SelectDropdownProps
    style?: React.CSSProperties
    prefix?: React.ReactNode
    onChange: (pageId: string) => void

    canCreatePage?: boolean
    onClickCreatePage?: () => void
}
const SCxTarget = styled.div``

const SCxDropDown = styled.div`
    max-height: 600px;
`
export const SelectPage: React.FunctionComponent<SelectPageProps> = ({
    value,
    width,
    languageList,
    options,
    placeholder = '请选择',
    dropdownProps,
    withinPortal,
    prefix,
    style,
    onChange,

    canCreatePage,
    onClickCreatePage
}) => {
    const [menuOpened, setMenuOpened] = useState(false)
    const [searchWord, setSearchWord] = useState('')
    const [openedMap, setOpenedMap] = useState<{ [key: string]: boolean }>(
        Object.fromEntries(languageList?.map(item => [item.lang, true]) ?? [])
    )

    const floatPageList = useMemo(() => floatPageTree(options, []), [options])

    const option = useMemo(() => floatPageList?.find(item => item.id === value), [floatPageList, value])

    const langFilterOptions = useMemo(() => {
        return reduce<PageItem, BasePage[][]>(
            (p, c) => {
                if (c.type === 'GROUP') {
                    return p
                }
                const index = findIndex<ApplicationSettingLanguageItem>(langItem => langItem.lang === c.language, languageList ?? [])
                if (index !== -1 && c.name?.includes(searchWord)) {
                    p[index].push(c)
                }
                return p
            },
            [[], [], []],
            floatPageList
        )
    }, [floatPageList, languageList, searchWord])

    const isEmpty = langFilterOptions.every(item => item.length === 0)

    const inputValue = useMemo(() => {
        if (!value) {
            return
        }
        if (value === LOGIN_PAGE) {
            return '登录页面'
        }

        if (!option) {
            return '未找到页面'
        }

        return typeof option.name === 'string' ? option.name || NO_NAME_PAGE : NO_NAME_PAGE
    }, [option, value])

    const singleLangOptions =
        searchWord === '' ? options.filter(option => option.language === languageList?.[0]?.lang) : langFilterOptions[0]

    const handleChange = useCallback(
        (pageId: string) => {
            onChange(pageId)
            setMenuOpened(false)
        },
        [onChange]
    )

    const renderPageList = useCallback(
        (list: PageItem[], initPadding?: number) => {
            return list.map(item => {
                return (
                    <SelectItem
                        key={item.id}
                        data={item}
                        value={value}
                        initPadding={initPadding}
                        isShowDesc={!!searchWord}
                        onSelect={handleChange}
                    />
                )
            })
        },
        [handleChange, searchWord, value]
    )

    return (
        <Menu
            opened={menuOpened}
            position="bottom-end"
            offsetOptions={{
                mainAxis: 8
            }}
            onChange={setMenuOpened}
            withinPortal={withinPortal}
            width={width || 300}
        >
            <Menu.Target>
                <SCxTarget>
                    <Input
                        value={inputValue}
                        readOnly
                        styles={{
                            input: {
                                cursor: 'pointer'
                            }
                        }}
                        prefix={option ? prefix : undefined}
                        suffix={<SC.Icon type="ArrowDownSmall" />}
                        placeholder={placeholder}
                        style={style}
                    />
                </SCxTarget>
            </Menu.Target>
            <Menu.Dropdown>
                <SCxDropDown>
                    <Flex style={{ padding: '4px 12px 0 12px', marginBottom: 8 }}>
                        <Input
                            style={{ width: '100%' }}
                            value={searchWord}
                            clearable
                            prefix={<IconFont type="Search" color="var(--color-gray-400)" />}
                            onChange={e => setSearchWord(e.target.value)}
                            placeholder="搜索"
                            onClear={() => setSearchWord('')}
                        />
                    </Flex>
                    {isEmpty ? (
                        <Empty
                            style={{ height: 134 }}
                            styles={{
                                content: {
                                    color: 'var(--color-gray-500)'
                                }
                            }}
                            description="没有数据"
                            icon={<IconFont size={32} type="NoData" fill="var(--color-gray-500)" />}
                        />
                    ) : (languageList ?? []).length > 1 ? (
                        languageList?.map((langItem, i) => {
                            const langOptions =
                                searchWord === '' ? options.filter(option => option.language === langItem.lang) : langFilterOptions[i]
                            if (langOptions.length === 0) {
                                return null
                            }
                            return (
                                <Group
                                    label={
                                        <Flex
                                            gap={4}
                                            onClick={e => {
                                                e.stopPropagation()
                                                setOpenedMap({ ...openedMap, [langItem.lang]: !openedMap[langItem.lang] })
                                            }}
                                        >
                                            <SC.Icon type={openedMap[langItem.lang] ? 'ArrowDownSmall' : 'ArrowRightSmall'} />
                                            <Text weight={400}>{langItem.title}</Text>
                                        </Flex>
                                    }
                                    styles={{
                                        wrapper: {
                                            height: 36
                                        },
                                        root: {
                                            padding: '0!important'
                                        },
                                        collapse: {
                                            padding: '0px!important'
                                        }
                                    }}
                                    opened={openedMap[langItem.lang]}
                                    renderRightIcon={() => null}
                                    key={langItem.lang}
                                    onCollapseChange={opened => setOpenedMap({ ...openedMap, [langItem.lang]: opened })}
                                >
                                    {renderPageList(langOptions, 36)}
                                    {canCreatePage && (
                                        <Menu.Item
                                            styles={{ item: { color: 'var(--color-main)', gap: 6, padding: '10px 38px' } }}
                                            icon={<IconFont type="Add" size={16} />}
                                            onClick={onClickCreatePage}
                                        >
                                            添加页面
                                        </Menu.Item>
                                    )}
                                </Group>
                            )
                        })
                    ) : (
                        <div>
                            {renderPageList(singleLangOptions, 16)}
                            {canCreatePage && (
                                <Menu.Item
                                    styles={{ item: { color: 'var(--color-main)', gap: 6, padding: '10px 38px' } }}
                                    icon={<IconFont type="Add" size={16} />}
                                    onClick={onClickCreatePage}
                                >
                                    添加页面
                                </Menu.Item>
                            )}
                        </div>
                    )}
                    {searchWord === '' && (
                        <>
                            <Divider style={{ margin: '8px 0' }} />
                            <Menu.Item
                                styles={{ item: { gap: 6, padding: '8px 38px' } }}
                                icon={<IconFont type="SignIn" size={16} color="var(--color-gray-400)" />}
                                onClick={e => handleChange(LOGIN_PAGE)}
                            >
                                <Flex gap={4} alignItems="center">
                                    <Text>登录页面</Text>
                                    <Tag
                                        background="var(--color-gray-100)"
                                        color="var(--color-gray-400)"
                                        style={{ fontSize: 12, textAlign: 'center', lineHeight: '12px' }}
                                        radius={4}
                                    >
                                        系统
                                    </Tag>
                                </Flex>
                            </Menu.Item>
                            {/* <SC.Item
                                isHover
                                style={{ padding: '0 16px', height: 36, display: 'flex', flexWrap: 'nowrap' }}
                                onClick={e => handleChange(LOGIN_PAGE)}
                            >
                                <SC.Wrapper>
                                    <div style={{ flex: 1, display: 'flex', height: '100%', alignItems: 'center', gap: 4 }}>
                                        <SC.Icon type="SignIn" />
                                        <Text weight={400} size={14}>
                                            登录页面
                                        </Text>
                                        <Tag
                                            background="var(--color-gray-100)"
                                            color="var(--color-gray-400)"
                                            style={{ fontSize: 12, textAlign: 'center', lineHeight: '12px' }}
                                            radius={4}
                                        >
                                            系统
                                        </Tag>
                                    </div>
                                    {value === LOGIN_PAGE && <SC.Icon style={{ flexShrink: 0 }} type="Tick" color="var(--color-theme-7)" />}
                                </SC.Wrapper>
                            </SC.Item> */}
                        </>
                    )}
                </SCxDropDown>
            </Menu.Dropdown>
        </Menu>
    )
}
